import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";
import { formatMoney, shopCheckout, shopPrice, apiError } from "@/lib/api";

const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "long" });
const inputCls = "w-full rounded-lg border border-[#0f2544]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1f44e0]";

export default function EbookShop({ book }) {
  const [venta, setVenta] = useState(book.venta);
  const cur = venta?.moneda || "EUR";
  const formRef = useRef(null);
  const [params, setParams] = useSearchParams();
  const p = venta;

  useEffect(() => { shopPrice(book.id).then(setVenta).catch(() => {}); }, [book.id]);

  useEffect(() => {
    if (params.get("pago") === "cancelado") {
      toast.info("Has cancelado el pago. Puedes intentarlo de nuevo cuando quieras.", { id: "pago-cancelado" });
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#comprar-ebook") {
      setTimeout(() => document.getElementById("comprar-ebook")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
  }, [hash, book.id]);

  const [busy, setBusy] = useState(false);
  if (!venta) return null;
  const pay = async (e) => {
    e.preventDefault();
    const f = formRef.current;
    setBusy(true);
    try {
      const { checkout_url } = await shopCheckout({ book_id: book.id, nombre: f.nombre.value.trim(), email: f.email.value.trim(), acepta_condiciones: f.acepta.checked });
      window.location.href = checkout_url;
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  return (
    <div id="comprar-ebook" className="mt-8 rounded-2xl border border-[#0f2544]/12 bg-white/60 p-6 max-w-md scroll-mt-28" data-testid="ebook-shop">
      <div className="flex items-center justify-between gap-3">
        <p className="hv-overline">Ebook EPUB · Venta directa</p>
        <span className="text-xs font-semibold text-[#0f2544]/70" data-testid="ebook-currency">{cur === "EUR" ? "Precio en euros" : "Precio en dólares (USD)"}</span>
      </div>
      <div className="flex items-baseline gap-3 mt-3">
        <span className="font-serif text-3xl font-semibold text-[#0f2544]" data-testid="ebook-price">{formatMoney(p.final, cur)}</span>
        {venta.descuento > 0 && <span className="text-[#8a8a83] line-through" data-testid="ebook-original-price">{formatMoney(p.precio, cur)}</span>}
        {venta.descuento > 0 && <span className="rounded-full bg-[#1f44e0] px-2.5 py-0.5 text-xs font-semibold text-white" data-testid="ebook-discount">-{venta.descuento}%</span>}
      </div>
      {venta.descuento > 0 && <p className="text-xs text-[#1f44e0] mt-1">{venta.oferta_nombre ? `${venta.oferta_nombre} · ` : ""}Oferta válida hasta el {fmtDate(venta.oferta_fin)}</p>}
      <form ref={formRef} onSubmit={pay} className="grid gap-3 mt-5">
        <input name="nombre" required minLength={2} placeholder="Nombre y apellidos (para la factura)" className={inputCls} data-testid="ebook-nombre" />
        <input name="email" required type="email" placeholder="Email donde recibirás el libro" className={inputCls} data-testid="ebook-email" />
        <label className="flex items-start gap-2.5 text-[0.82rem] leading-relaxed text-[#0f2544]/85">
          <input name="acepta" type="checkbox" required className="accent-[#1f44e0] mt-0.5 shrink-0" data-testid="ebook-acepta" />
          <span>He leído y acepto las <Link to="/condiciones-de-venta" target="_blank" className="underline text-[#0f2544]">Condiciones de Venta</Link>. Solicito la entrega inmediata del ebook y acepto que, una vez disponible la descarga, pierdo el derecho de desistimiento.</span>
        </label>
        <button type="submit" disabled={busy} className="mt-1 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0f2544] px-6 py-3.5 text-xs font-semibold tracking-[0.12em] text-white hover:bg-[#1f44e0] transition-colors disabled:opacity-60" data-testid="ebook-pay-btn">
          <Lock size={14} /> {busy ? "REDIRIGIENDO AL PAGO SEGURO..." : `COMPRAR · ${formatMoney(p.final, cur)}`}
        </button>
      </form>
      <p className="flex items-center gap-1.5 text-xs text-[#8a8a83] mt-3"><ShieldCheck size={13} /> Pago seguro con tarjeta, Apple Pay o Google Pay a través de Stripe. Descarga inmediata (2 descargas, 15 días) y factura por email.</p>
    </div>
  );
}
